import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_TICKET_MESSAGE,
  GET_TICKET_MESSAGES,
} from "./TicketChatModal.queries";
import useStyles from "./TicketChatModal.styles";
import ConfigurableValues from "../../config/constants";
import useAttachmentUpload from "./useAttachmentUpload";

function TicketChatModal({ visible, onHide, ticketId, ticketQuery }) {
  const classes = useStyles();
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const {
    selectedAttachments,
    uploadingAttachments,
    uploadError,
    handleFileSelection,
    removeAttachment,
    clearAttachments,
    setUploadError,
  } = useAttachmentUpload({
    cloudinaryUploadUrl: CLOUDINARY_UPLOAD_URL,
    cloudinaryPreset: CLOUDINARY_FOOD,
    maxFiles: 5,
  });

  const { data: ticketData, loading: ticketLoading } = useQuery(ticketQuery, {
    variables: { ticketId },
    skip: !visible || !ticketId,
    fetchPolicy: "network-only",
  });

  const {
    data,
    previousData,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useQuery(GET_TICKET_MESSAGES, {
    variables: {
      input: { ticket: ticketId, page: 1, limit: 50 },
    },
    skip: !visible || !ticketId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: false,
  });

  const [sendMessage] = useMutation(CREATE_TICKET_MESSAGE, {
    onCompleted: () => {
      setMessage("");
      clearAttachments();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setIsSending(false);
      refetch();
    },
    onError: () => {
      setIsSending(false);
    },
  });

  useEffect(() => {
    if (visible && ticketId) {
      refetch();
      startPolling(4000);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [visible, ticketId, startPolling, stopPolling, refetch]);

  const resolvedData = data || previousData;
  const messages = useMemo(() => {
    return resolvedData?.getTicketMessages?.messages || [];
  }, [resolvedData]);
  const ticket =
    resolvedData?.getTicketMessages?.ticket ||
    ticketData?.getSingleSupportTicket;
  const isClosed = ticket?.status === "closed";

  const orderedMessages = useMemo(() => {
    return [...messages].sort(
      (a, b) => parseInt(a.createdAt, 10) - parseInt(b.createdAt, 10),
    );
  }, [messages]);

  const formatTimestamp = (timestamp) => {
    try {
      const date = new Date(parseInt(timestamp, 10));
      const now = Date.now();
      const isOlderThan24Hours = now - date.getTime() > 24 * 60 * 60 * 1000;
      const hours = date.getHours() % 12 || 12;
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const timePart = `${hours}:${minutes}`;
      if (!isOlderThan24Hours) {
        return timePart;
      }
      return `${timePart} ${date.toLocaleDateString()}`;
    } catch (error) {
      return "unknown time";
    }
  };

  const handleAttachmentClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const scrollToLatestMessage = (behavior = "auto") => {
    if (!messagesEndRef.current) return;
    messagesEndRef.current.scrollIntoView({ behavior, block: "end" });
  };

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (
      (!trimmedMessage && selectedAttachments.length === 0) ||
      isSending ||
      uploadingAttachments
    )
      return;
    setIsSending(true);
    try {
      sendMessage({
        variables: {
          messageInput: {
            content: trimmedMessage || null,
            attachments: selectedAttachments.map((item) => item.url),
            ticket: ticketId,
          },
        },
      });
    } catch (error) {
      setIsSending(false);
      setUploadError(error.message || "Failed to upload attachment(s)");
    }
  };

  const handleClose = () => {
    stopPolling();
    onHide();
  };

  const formatStatus = (status) => {
    if (!status) return "";
    if (status.toLowerCase() === "inprogress") return "In Progress";
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => {
      scrollToLatestMessage("auto");
    }, 0);
    return () => clearTimeout(timer);
  }, [visible, orderedMessages.length]);

  return (
    <Dialog
      open={visible}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      classes={{ paper: classes.dialogPaper }}
    >
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.headerTitle}>
            {ticket?.title || "Support Chat"}
          </Typography>
          <Typography className={classes.statusText}>
            {formatStatus(ticket?.status)}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          className={classes.closeButton}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <Box className={classes.body}>
        {ticket?.description ? (
          <Box className={classes.description}>
            <Typography className={classes.descriptionTitle}>
              Ticket Description
            </Typography>
            <Typography className={classes.descriptionText}>
              {ticket.description}
            </Typography>
            <Typography className={classes.messageTime}>
              {formatTimestamp(ticket.createdAt || Date.now().toString())}
            </Typography>
          </Box>
        ) : null}

        <Box className={classes.messageArea}>
          {!messages.length && (loading || ticketLoading) ? (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height="100%"
            >
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Typography color="error">Failed to load messages</Typography>
          ) : orderedMessages.length > 0 ? (
            orderedMessages.map((msg) => (
              <Box
                key={msg._id}
                className={`${classes.messageBubble} ${
                  msg.senderType === "user"
                    ? classes.messageUser
                    : classes.messageSupport
                }`}
              >
                {(msg.attachments || []).map((attachment, index) => (
                  <Box
                    key={`${msg._id}_attachment_${index}`}
                    className={classes.messageAttachment}
                  >
                    <img
                      src={attachment}
                      alt="attachment"
                      className={classes.attachmentImage}
                      onLoad={() => scrollToLatestMessage("auto")}
                    />
                  </Box>
                ))}
                {msg.content ? <Typography>{msg.content}</Typography> : null}
                <Typography className={classes.messageTime}>
                  {formatTimestamp(msg.createdAt)}
                </Typography>
              </Box>
            ))
          ) : (
            <Typography className={classes.helperText}>
              No messages yet
            </Typography>
          )}
          <div ref={messagesEndRef} />
        </Box>

        {isClosed ? (
          <Box className={classes.closedNotice}>
            This ticket is closed. You cannot send new messages.
          </Box>
        ) : (
          <Box className={classes.inputContainer}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              className={classes.hiddenInput}
              onChange={handleFileSelection}
            />
            <IconButton
              className={classes.attachButton}
              onClick={handleAttachmentClick}
              disabled={isSending}
            >
              <AttachFileIcon />
            </IconButton>
            <Box className={classes.inputArea}>
              {selectedAttachments.length > 0 ? (
                <Box className={classes.attachmentList}>
                  {selectedAttachments.map((item, index) => (
                    <Box
                      key={`${item.name}_${index}`}
                      className={classes.attachmentChip}
                    >
                      <Typography className={classes.attachmentName}>
                        {item.name}
                      </Typography>
                      <button
                        type="button"
                        className={classes.removeAttachmentButton}
                        onClick={() => removeAttachment(index)}
                        aria-label="Remove attachment"
                      >
                        x
                      </button>
                    </Box>
                  ))}
                </Box>
              ) : null}
              {uploadError ? (
                <Typography color="error" variant="caption">
                  {uploadError}
                </Typography>
              ) : null}
              {uploadingAttachments ? (
                <Typography variant="caption">
                  Uploading attachment...
                </Typography>
              ) : null}
              <textarea
                className={classes.input}
                placeholder="Type your message here..."
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                disabled={isSending}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
              />
            </Box>
            <button
              type="button"
              className={`${classes.sendButton} ${(!message.trim() && selectedAttachments.length === 0) || isSending ? classes.disabledButton : ""}`}
              onClick={handleSendMessage}
              disabled={
                (!message.trim() && selectedAttachments.length === 0) ||
                isSending ||
                uploadingAttachments
              }
            >
              {isSending ? "Sending..." : "Send"}
            </button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

export default TicketChatModal;
